import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Code, Copy, Check, ArrowSquareOut, QrCode, Printer, Download } from "@phosphor-icons/react";

/**
 * ReviewsWidgetPanel — VAGUE 25.
 *
 * Ready-to-paste embed snippet for Premium merchants. Two integration modes :
 * - JS snippet (2 lines) → auto-renders inline
 * - iframe (1 line) → CMS-friendly fallback
 *
 * A live preview iframe shows exactly what visitors will see.
 */
export default function ReviewsWidgetPanel({ business }) {
  const [snippet, setSnippet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");
  const [mode, setMode] = useState("js");
  const [qrTarget, setQrTarget] = useState("profile");

  useEffect(() => {
    if (!business?.slug) return;
    (async () => {
      try {
        const r = await api.get(`/public/widgets/reviews/${business.slug}/snippet`);
        setSnippet(r.data);
      } catch (e) {
        toast.error("Impossible de charger le widget : " + e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [business?.slug]);

  const copy = async (which, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      toast.success("✅ Code copié — colle-le dans ton site");
      setTimeout(() => setCopied(""), 2500);
    } catch (_) {
      toast.error("Impossible de copier — sélectionne le texte manuellement");
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-sm p-6" data-testid="widget-panel-loading">
        <div className="text-sm text-muted-foreground italic">Chargement du widget…</div>
      </div>
    );
  }
  if (!snippet) return null;

  const notPremium = !snippet.premium;
  const currentCode = mode === "js" ? snippet.snippet_js : snippet.snippet_iframe;

  return (
    <div className="bg-card border border-border rounded-sm p-6" data-testid="reviews-widget-panel">
      <div className="flex items-center gap-2 mb-2">
        <Code size={22} weight="duotone" className="text-primary" />
        <h3 className="font-display text-lg font-medium">Widget d'avis pour ton site</h3>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
        Affiche tes avis MonCommerce68 sur ton propre site en 30 secondes. Copie
        le code, colle-le où tu veux dans ton HTML, tes visiteurs voient tes
        étoiles en direct.
      </p>

      {notPremium && (
        <div className="mb-4 bg-secondary/10 border border-secondary/40 rounded-sm p-3 text-sm">
          <div className="font-semibold text-secondary mb-1">Fonctionnalité Premium</div>
          <div className="text-foreground/80">
            Le widget affichera un encart d'invitation à passer Premium tant que
            ton abonnement n'est pas actif.
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-3 bg-muted/50 rounded-sm p-1 w-fit">
        {[
          { id: "js", label: "Script JS (recommandé)" },
          { id: "iframe", label: "iframe (CMS)" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setMode(t.id)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-sm transition-colors ${
              mode === t.id
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`widget-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Code block */}
      <div className="relative">
        <pre
          className="bg-[#1a1b1e] text-[#e6e6e6] text-xs p-3 pr-14 rounded-sm overflow-x-auto whitespace-pre-wrap break-all leading-relaxed"
          data-testid={`widget-code-${mode}`}
        >
          <code>{currentCode}</code>
        </pre>
        <button
          onClick={() => copy(mode, currentCode)}
          className={`absolute top-2 right-2 inline-flex items-center gap-1 text-xs font-bold px-2 py-1.5 rounded-sm ${
            copied === mode
              ? "bg-emerald-500 text-white"
              : "bg-white text-[#1a1b1e] hover:bg-primary hover:text-primary-foreground"
          }`}
          data-testid={`widget-copy-${mode}`}
        >
          {copied === mode ? <Check size={12} weight="bold" /> : <Copy size={12} weight="bold" />}
          {copied === mode ? "Copié !" : "Copier"}
        </button>
      </div>

      {/* Preview */}
      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">
          Aperçu en direct
        </div>
        <div className="border border-border rounded-sm bg-[#F9F8F6] p-3">
          <iframe
            src={snippet.urls.html}
            className="w-full block bg-transparent"
            style={{ height: 460, border: 0 }}
            title="Aperçu du widget d'avis"
            data-testid="widget-preview-iframe"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-3 text-xs">
        <a
          href={snippet.urls.html}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:text-secondary font-semibold"
          data-testid="widget-open-html"
        >
          <ArrowSquareOut size={12} weight="bold" /> Ouvrir le widget dans un onglet
        </a>
        <a
          href={snippet.urls.json}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground font-semibold"
          data-testid="widget-open-json"
        >
          <ArrowSquareOut size={12} weight="bold" /> Voir les données brutes (JSON)
        </a>
      </div>

      {/* ---------- QR CODE (VAGUE 26) ---------- */}
      <div className="mt-6 pt-6 border-t border-border" data-testid="widget-qr-section">
        <div className="flex items-center gap-2 mb-2">
          <QrCode size={20} weight="duotone" className="text-primary" />
          <h4 className="font-display text-base font-medium">QR code pour ta vitrine / ton menu</h4>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
          Imprime ton QR code et colle-le dans ta vitrine, sur ton menu papier ou
          ta carte de visite. Les clients scannent et voient directement tes avis.
        </p>

        {/* Target toggle */}
        <div className="flex gap-1 mb-4 bg-muted/50 rounded-sm p-1 w-fit">
          {[
            { id: "profile", label: "→ Fiche complète (recommandé)" },
            { id: "widget", label: "→ Widget avis seul" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setQrTarget(t.id)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-sm transition-colors ${
                qrTarget === t.id
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`qr-target-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-[auto_1fr] gap-5 items-start">
          <div className="bg-white border border-border rounded-sm p-3 w-fit">
            <img
              key={qrTarget}
              src={`${snippet.urls.qr_png}?target=${qrTarget}&size=10`}
              alt="QR code"
              className="w-40 h-40 block"
              data-testid="widget-qr-image"
            />
          </div>
          <div className="space-y-2">
            <a
              href={`${snippet.urls.qr_png}?target=${qrTarget}&size=16`}
              download={`qr-moncommerce68-${business.slug}.png`}
              className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground hover:bg-[#0f3d29] px-3 py-2 rounded-sm text-sm font-semibold w-fit"
              data-testid="widget-qr-download-png"
            >
              <Download size={14} weight="bold" /> Télécharger en PNG
            </a>
            <div className="text-[11px] text-muted-foreground">
              Idéal pour les sticker vitrine, cartes de visite ou tampons.
            </div>
            <a
              href={`${snippet.urls.qr_svg}?target=${qrTarget}`}
              download={`qr-moncommerce68-${business.slug}.svg`}
              className="inline-flex items-center gap-1.5 border border-primary text-primary hover:bg-primary/5 px-3 py-2 rounded-sm text-sm font-semibold w-fit"
              data-testid="widget-qr-download-svg"
            >
              <Download size={14} weight="bold" /> Télécharger en SVG (impression pro)
            </a>
            <a
              href={`/api/public/widgets/reviews/${business.slug}/qr-card.html?target=${qrTarget}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 bg-secondary text-secondary-foreground hover:bg-[#b3684a] px-3 py-2 rounded-sm text-sm font-semibold w-fit"
              data-testid="widget-qr-open-card"
            >
              <Printer size={14} weight="bold" /> Ouvrir la carte A6 imprimable
            </a>
            <div className="text-[11px] text-muted-foreground pt-1">
              La carte A6 s'ouvre dans un nouvel onglet — clique sur "🖨 Imprimer"
              pour l'imprimer sur du papier standard.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
